import React, {useMemo, useState} from 'react'

import * as prosConnect from "../../prodect/ProsConnect";
import {
    ATTR_OBJ_ID,
    ATTR_OBJ_STATE,
    OBJ_STATE_ACT,
    UI_LISTED_ELEMENTS_LIMIT
} from "../../prodect/ProsConnect";
import BasicButton, {CancelButton, ConfirmButton} from '../../components/BasicButton';
import TextButton from '../../components/TextButton';
import Pagination from '../../components/Pagination';
import Spacer from '../../components/Spacer';
import ReportSearchMenu from "../../prodect/ReportSearchMenu";
import {MAX_REPORTS_POPUP_VISIBLE} from "../ProjectReports";
import {prosAppNotifWait, prosAppNotifWarnConfirm} from "../../prodect/ProsAppNotif";
import {isProsStringEmpty} from "../../prodect/ProsCommonUi";
import TabsNavigation from "../../components/TabsNavigation";

/**
 * @typedef Props
 * @property {number} maxItems If set, limit number of reports that can be selected
 * @property {string[]} preSelectedReportIds If set, set selected reports
 * @property {(reportIds: string[]) => void} onClose
 */

/**
 * @param {Props} props
 */
function SelectReportsModal(props) {
    const [showingSearchList, setShowingSearchList] = useState(props.isSearchMode)
    const [selectedReportIds, setSelectedReportIds] = useState(
        props.isSearchMode ? [] : props.preSelectedReportIds)
    const [searchTerm, setSearchTerm] = useState(props.reportSelectionTextFilter || '')
    const [startPage, setStartPage] = useState(0)

    const project = prosConnect.getActiveProject()
    const reportslist = project.getReportsOrSearches(showingSearchList)
    const allReportIds = reportslist.map(report => report[ATTR_OBJ_ID])
    const limitCount = props.maxItems ? props.maxItems : reportslist.length
    const maxItems = showingSearchList ? 1 : limitCount

    const searchResults = useMemo(() => {
        return reportslist?.filter(report => {
            const reportInfo = prosConnect.getReportInfo(report)
            return reportInfo.toLowerCase().includes(searchTerm.toLowerCase().trim())
        })
    }, [searchTerm, reportslist])

    const visibleReports = searchResults.slice(startPage, startPage + MAX_REPORTS_POPUP_VISIBLE)

    function unselectReport(id){
        const index = selectedReportIds.indexOf(id)
        setSelectedReportIds(reportIds => {
            // A copy is needed, directly mutating `reportIds` doesn't work
            const reportIdsCopy = [...reportIds]
            reportIdsCopy.splice(index, 1)
            return reportIdsCopy
        })
    }

    /**
     * @param {string} reportId 
     */
    function toggleReport(reportId) {
        if(maxItems === 1) {
            setSelectedReportIds([reportId])
            if(showingSearchList) props.onClose([reportId], searchTerm, showingSearchList)
            return
        }
        let index = selectedReportIds.indexOf(reportId)
        if (index === -1) {
            if (maxItems && getSelectedReportIds().length >= maxItems) {
                return
            }
            setSelectedReportIds(reportIds => [...reportIds, reportId])
        } else {
            unselectReport(reportId)
        }
    }

    function getSelectedReportIds() {
        return selectedReportIds.filter(reportId => allReportIds.includes(reportId))
    }

    function refreshModal(item, remove) {
        if (remove) {
            unselectReport(item[ATTR_OBJ_ID])
            if (visibleReports.length <= 1 && startPage) {
                // When removing last report on page select previous page
                setStartPage(startPage-MAX_REPORTS_POPUP_VISIBLE)
            }
        } else {
            // Added a new report - select last page to show it
            let firstOnLastPage = Math.trunc(selectedReportIds.length/MAX_REPORTS_POPUP_VISIBLE) * MAX_REPORTS_POPUP_VISIBLE
            setSelectedReportIds(reportIds => [...reportIds, item[ATTR_OBJ_ID]])
            setSearchTerm('')
            setStartPage(firstOnLastPage)
        }
    }

    function renderButton(report) {
        const isSelected = selectedReportIds.includes(report[ATTR_OBJ_ID])
        return <li
                className={isSelected ? "selected" : null}
                onClick={() => {
                    toggleReport(report[ATTR_OBJ_ID])
                }}
            >
            <span>{prosConnect.getReportInfo(report)}</span>
            {props.hideMenu ? null : <ReportSearchMenu
                report={report}
                notif={props.notif}
                updateModal={()=> refreshModal(report, true)}
                gotoPage={(a,b,c) =>{
                    props.gotoPage(a,b,c)//<--stupid but no time to figure out how to pass functionarguments to another function
                    props.onCancel()
                }}
            />}
        </li>
    }

    function renderCount() {
        const countSelected = getSelectedReportIds().length
        const countMax = maxItems ?? allReportIds.length
        return `Valittu ${countSelected}/${countMax} raporttia`
    }

    function renderSectionBottom(){
        if(showingSearchList) return null
        return <>
            <Spacer size={"lg"}/>
            <div className={"grid-container grid-flex-1 grid-flex-2_md grid-flex-3_lg"}>
                <div className={"element-set-v-sm"}>
                    <BasicButton
                        btnClass={"2nd"}
                        label={"Valitse kaikki"}
                        icon={"check-list"}
                        action={() => setSelectedReportIds(allReportIds)}
                        disabled={selectedReportIds.length === reportslist.length}
                        size={"md"}
                    />
                    <BasicButton
                        btnClass={"2nd"}
                        label={"Poista kaikki valinnat"}
                        icon={"times"}
                        action={() => setSelectedReportIds([])}
                        disabled={selectedReportIds.length === 0}
                        size={"md"}
                    />
                </div>
                <div className={"display-flex"}><p className={"valign-center text-center flex-grow text-center"}>{renderCount()}</p></div>
            </div>
        </>
    }

    function renderFooter() {
        if(showingSearchList) return null
        return <div className={"btn-set"}>
            <CancelButton
                action={props.onCancel}
            />
            <ConfirmButton
                label={"OK"}
                action={()=> props.onClose(selectedReportIds, searchTerm, showingSearchList)}
            />
        </div>
    }

    function addNewSearchButton() {
        if(prosConnect.getActiveProject().getAttr(ATTR_OBJ_STATE) !== OBJ_STATE_ACT
            && !prosConnect.hasPermissionTo('makeRepairWeb')) return null
        return <BasicButton
            icon={"plus"}
            label={"Uusi haku"}
            action={() => addNewSearchReport()}
        />
    }

    function addNewSearchReport() {
        const maxSearchLimit = prosConnect.featureLimit(prosConnect.FEAT_MAX_SEARCH_COUNT, 999)
        if (props.userCanAddSearches) {
            props.gotoPage('editSearch', true, "newSearch")
            props.onCancel()
        } else {
            prosAppNotifWarnConfirm( "Palvelutason hakujen maksimimäärä rajattu: " + maxSearchLimit)
        }
    }

    function addNewReportOrSearchButton() {
        if(!prosConnect.hasPermissionTo("editProject", undefined, undefined, false)) return null
        if(showingSearchList){
            return addNewSearchButton()
        }
        return <TextButton
            label={"Uusi raportti"}
            placeholder="Kirjoita raportin nimi"
            onSubmit={(val)=> {
                createNewReport(val)
            }}
        />
    }

    const createNewReport = async (textValue)=> {
        if (isProsStringEmpty(textValue)) {
            prosAppNotifWarnConfirm("Nimi puuttuu", true)
            return
        }
        prosAppNotifWait("Tallennetaan..")
        let reportId = await prosConnect.getActiveProject().awaitAddReport(textValue, false)
        let report = prosConnect.getObjById(reportId)
        refreshModal(report, false)
    }

    function toggleReportOrSearch(isNextSearchMode) {
        setStartPage(0)
        setSearchTerm('')
        setShowingSearchList(isNextSearchMode)
        // when toggle from searcg to reports select originally selected reports
        setSelectedReportIds(isNextSearchMode ? []  : props.preSelectedReportIds)
    }

    function renderToggleButtons(){
        if(props.disableSearchMode) return null
        const linkArr = ['Raportit', 'Haut']
        let linkValues = linkArr.map((phase, i) => {
            return {
                value: !!i,
                text: phase,
                selected: showingSearchList === !!i,
            }
        })
        return <TabsNavigation
            values={linkValues}
            callback={(val)=> toggleReportOrSearch(val)}
        />
    }

    function changePage(pageNumber) {
        setStartPage(pageNumber)
    }

    function renderInfoText(){
        return reportslist.length < 1 ? <h6 className={"color-2nd text-center margin-top"}>Ei raportteja.</h6> : null
    }
    function renderControls() {
        return <>
            {renderInfoText()}
            <div className={"btn-set margin-bottom"}>
                {reportslist.length > UI_LISTED_ELEMENTS_LIMIT ? <TextButton
                    label={"Suodata"}
                    inputType={"search"}//go in pairs
                    keepActive={true}//go in pairs
                    value={searchTerm}
                    onSubmit={val => setSearchTerm(val)}
                    isActive={searchTerm.length > 0}
                /> : null}
                {addNewReportOrSearchButton()}
            </div>
        </>
    }

    function renderVisibleList(){
        if(searchTerm.length > 0 && searchResults < 1) return <p>Haulla ei tuloksia.</p>
        return visibleReports.map((report, i) => {
            return <React.Fragment key={i}>
                {renderButton(report)}
            </React.Fragment>
        })
    }

    return <>
        <section id={"reports-popup"} className={"element-set-v"}>
            {renderToggleButtons()}
            {renderControls()}
            <div className={"flex-grow"}>
                <ul className={"tag-collection"}>
                    {renderVisibleList()}
                </ul>
            </div>
                {searchResults.length > MAX_REPORTS_POPUP_VISIBLE
                    &&
                    <Pagination
                        btnClass={"3rd"}
                        allItemsInt={searchResults.length}
                        chunk={MAX_REPORTS_POPUP_VISIBLE}
                        currentPage={startPage}
                        callback={changePage}
                        showPages
                        className={"btn-set_center"}
                    />
                }
            {renderSectionBottom()}
        </section>
        <footer className="popup-footer">
        {renderFooter()}
        </footer>
    </>
}

/**
 * @typedef OpenSelectReportsModalParams
 * @property {number} maxItems If set, limit number of reports that can be selected
 * @property {string[]} preSelectedReportIds If set, set selected reports
 * @property {(reportIds: string[]) => void} onClose
 */

/**
 * @param {import('../../components/Modal').ModalContext} modalContext
 * @param {OpenSelectReportsModalParams} params
 */
export function openSelectReportsModal(modalContext, params) {
    const content = <SelectReportsModal
        {...params}
        onClose={(reportIds, searchTerm, isSearchMode) => {
            params.onClose(reportIds, searchTerm, isSearchMode)
            modalContext.closeModal()
        }}
        onCancel={modalContext.closeModal}
    />
    modalContext.openModal({
        heading: 'Valitse raportit',
        content
    })
}